<script>
import { ElNotification, ElMessageBox } from "element-plus";
import {
  View,
  Download,
  Document,
  Close,
  FolderDelete,
  Delete,
} from "@element-plus/icons-vue";
import debounce from "lodash/debounce";
import { formatterPrice, roundNumber } from "@/utils/common";

const DEFAULT_SORT = { sort_by: "created_at", sort_order: "desc" };

export default {
  name: "QuarterReport",
  components: { View, Download, Document, Close, FolderDelete, Delete },
  data() {
    return {
      loading: false,
      loadingBalance: false,
      uploading: false,
      uploadedFileName: "",
      fin_year: new Date().getFullYear(),
      uploadAccept:
        ".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel",

      // ── Mahalla loyihalari (quarter-projects) ──
      filtersVisible: true,
      tableLoading: false,
      exporting: false,
      projects: [],
      total: 0,
      page: 1,
      pageSize: 20,
      sortBy: DEFAULT_SORT.sort_by,
      sortOrder: DEFAULT_SORT.sort_order,
      filters: {
        year: null,
        region_id: null,
        district_id: null,
        quarter_id: null,
        status: null,
        category: null,
        outcome_type: null,
        ai_status: null,
        search: "",
      },
      regions: [],
      districts: [],
      quarters: [],
      regionsLoading: false,
      districtsLoading: false,
      quartersLoading: false,

      detailVisible: false,
      selectedProject: null,
      archivingId: null,
      deletingId: null,
      fileBaseUrl: process.env.VUE_APP_BASE_API_URL + "api/v2/info/file/",

      statusOptions: [
        { value: "AI_REVIEWING", label: "AI tekshiruvida" },
        { value: "AI_REJECTED", label: "AI rad etdi" },
        { value: "ACTIVE", label: "Faol" },
        { value: "CANCELLED", label: "Bekor qilingan" },
        { value: "COMPLETED", label: "Yakunlangan" },
        { value: "ARCHIVE", label: "Arxivlangan" },
      ],
      categoryOptions: [
        { value: "ROAD", label: "Yo'l" },
        { value: "LIGHTS", label: "Yoritish" },
        { value: "GAS", label: "Gaz" },
        { value: "WATER", label: "Suv" },
        { value: "GOV_BUILDING_REPAIR", label: "Davlat binosi ta'miri" },
        { value: "GOV_BUILDING_EQUIPMENT", label: "Davlat binosi jihozi" },
        { value: "BUILD_AND_EQUIPMENT", label: "Qurilish va jihoz" },
        { value: "FAMILY_FINANCIAL_SUPPORT", label: "Oilaviy moliyaviy yordam" },
        { value: "INFRA_DEVELOPMENT", label: "Infratuzilma rivoji" },
        { value: "OTHERS", label: "Boshqalar" },
      ],
      outcomeOptions: [
        { value: "BUY", label: "Sotib olish" },
        { value: "REPAIR", label: "Ta'mirlash" },
      ],
      aiStatusOptions: [
        { value: "PENDING", label: "Kutilmoqda" },
        { value: "APPROVED", label: "Tasdiqlangan" },
        { value: "REJECTED", label: "Rad etilgan" },
      ],
    };
  },
  computed: {
    isAdmin() {
      return this.$store.state.role === "admin";
    },
    years() {
      const currentYear = new Date().getFullYear();
      const years = [];

      for (let i = 2024; i <= currentYear; i += 1) {
        years.push(i);
      }

      return years;
    },
    defaultSort() {
      return {
        prop: this.sortBy,
        order: this.sortOrder === "asc" ? "ascending" : "descending",
      };
    },
    paidPercent() {
      const p = this.selectedProject;
      if (!p || !p.planned_amount) return 0;
      const pct = (Number(p.paid_amount) / Number(p.planned_amount)) * 100;
      if (!isFinite(pct) || pct < 0) return 0;
      return Math.min(100, Math.round(pct));
    },
    hasActiveFilters() {
      const f = this.filters;
      return Boolean(
        f.year ||
          f.region_id ||
          f.district_id ||
          f.quarter_id ||
          f.status ||
          f.category ||
          f.outcome_type ||
          f.ai_status ||
          (f.search && f.search.trim())
      );
    },
  },
  created() {
    this.debouncedSearch = debounce(() => {
      this.applyFilters();
    }, 400);
  },
  mounted() {
    this.getRegions();
    this.fetchProjects();
  },
  beforeUnmount() {
    this.debouncedSearch?.cancel?.();
  },
  methods: {
    // ───────────────────────── Mavjud (o'zgarmaydi) ─────────────────────────
    isExcelFile(file) {
      if (!file) {
        return false;
      }

      const allowedTypes = [
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "application/vnd.ms-excel",
      ];
      const fileName = String(file.name || "").toLowerCase();

      return (
        allowedTypes.includes(file.type) ||
        fileName.endsWith(".xlsx") ||
        fileName.endsWith(".xls")
      );
    },
    resetFileInput() {
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = "";
      }
    },
    triggerFileUpload() {
      if (this.loading || this.uploading) {
        return;
      }

      this.$refs.fileInput?.click();
    },
    async handleFileChange(event) {
      const file = event?.target?.files?.[0];

      if (!file) {
        return;
      }

      if (!this.isExcelFile(file)) {
        ElNotification({
          title: "Xatolik",
          message: "Faqat Excel fayllar (.xlsx, .xls) yuklash mumkin",
          type: "error",
        });
        this.resetFileInput();
        return;
      }

      this.uploading = true;

      try {
        const formData = new FormData();
        formData.append("file", file);

        await this.$http.post("api/v1/quarters/statistics-excel", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });

        this.uploadedFileName = file.name;

        ElNotification({
          title: "Muvaffaqiyatli",
          message: "Mahalla budjetining yillik prognozi muvaffaqiyatli yuklandi",
          type: "success",
        });
      } catch ({ response }) {
        ElNotification({
          title: "Xatolik",
          message:
            response?.data?.message || "Fayl yuklashda xatolik sodir bo'ldi!",
          type: "error",
        });
      } finally {
        this.uploading = false;
        this.resetFileInput();
      }
    },
    downloadBlob(res, fallbackName) {
      const url = URL.createObjectURL(
        new Blob([res.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
        })
      );
      const header = res.headers.get("Content-Disposition");
      let fileName = fallbackName;
      if (header) {
        const parts = header.split(";");
        if (parts[1]) {
          fileName = parts[1].split("=")[1].trim().replace(/"/g, "");
        }
      }
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },
    downloadBalanceExcel() {
      this.loadingBalance = true;
      this.$http
        .get("api/v1/quarter-payments/balances-excel", {
          responseType: "blob",
        })
        .then((res) => {
          this.downloadBlob(res, "Mahalla-balansi.xlsx");
        })
        .catch(({ response }) => {
          ElNotification({
            title: "Ogohlantirish",
            message:
              response?.data?.message ||
              "Ma'lumot yuklashda xatolik sodir bo'ldi!",
            type: "warning",
          });
        })
        .finally(() => {
          this.loadingBalance = false;
        });
    },
    downloadExcel() {
      this.loading = true;
      this.$http
        .get(`api/v1/quarter-payments/excel?fin_year=${this.fin_year}`, {
          responseType: "blob",
        })
        .then((res) => {
          this.downloadBlob(res, "Mahalla-tolovlari.xlsx");
        })
        .catch(({ response }) => {
          ElNotification({
            title: "Ogohlantirish",
            message:
              response?.data?.message ||
              "Ma'lumot yuklashda xatolik sodir bo'ldi!",
            type: "warning",
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },

    // ─────────────────────── Mahalla loyihalari ───────────────────────
    buildQueryParams(withPaging = true) {
      const f = this.filters;
      const params = {};
      if (withPaging) {
        params.limit = this.pageSize;
        params.offset = (this.page - 1) * this.pageSize;
      }
      if (f.year) params.year = f.year;
      if (f.region_id) params.region_id = f.region_id;
      if (f.district_id) params.district_id = f.district_id;
      if (f.quarter_id) params.quarter_id = f.quarter_id;
      if (f.status) params.status = f.status;
      if (f.category) params.category = f.category;
      if (f.outcome_type) params.outcome_type = f.outcome_type;
      if (f.ai_status) params.ai_status = f.ai_status;
      if (f.search && f.search.trim()) params.search = f.search.trim();
      params.sort_by = this.sortBy;
      params.sort_order = this.sortOrder;
      return params;
    },
    fetchProjects() {
      this.tableLoading = true;
      this.$http
        .get("api/v1/quarter-projects", { params: this.buildQueryParams(true) })
        .then(({ data }) => {
          this.projects = data.projects || [];
          this.total = data.count || 0;
        })
        .catch(({ response }) => {
          if (response?.status === 401) return;
          ElNotification({
            title: "Xatolik",
            message:
              response?.data?.message ||
              "Loyihalarni yuklashda xatolik sodir bo'ldi!",
            type: "error",
          });
        })
        .finally(() => {
          this.tableLoading = false;
        });
    },
    applyFilters() {
      this.page = 1;
      this.fetchProjects();
    },
    getRegions() {
      this.regionsLoading = true;
      this.$http
        .get("api/v1/regions")
        .then(({ data }) => {
          this.regions = data.regions || [];
        })
        .catch(() => {})
        .finally(() => {
          this.regionsLoading = false;
        });
    },
    onRegionChange() {
      this.filters.district_id = null;
      this.filters.quarter_id = null;
      this.districts = [];
      this.quarters = [];
      if (this.filters.region_id) {
        this.getDistricts();
      }
      this.applyFilters();
    },
    getDistricts() {
      this.districtsLoading = true;
      this.$http
        .get("api/v1/districts", {
          // limit=99 majburiy: backend limit>=100 bo'lsa uni 10 ga qisqartiradi
          params: { region_id: this.filters.region_id, limit: 99 },
        })
        .then(({ data }) => {
          this.districts = data.districts || [];
        })
        .catch(() => {})
        .finally(() => {
          this.districtsLoading = false;
        });
    },
    onDistrictChange() {
      this.filters.quarter_id = null;
      this.quarters = [];
      if (this.filters.district_id) {
        this.getQuarters();
      }
      this.applyFilters();
    },
    getQuarters() {
      this.quartersLoading = true;
      // admin tokeni (this.$http avtomatik qo'shadi) — arxivlangan mahallalar ham keladi
      this.$http
        .get("api/v1/quarters", {
          params: {
            district_id: this.filters.district_id,
            region_id: this.filters.region_id,
          },
        })
        .then(({ data }) => {
          this.quarters = data.quarters || [];
        })
        .catch(() => {})
        .finally(() => {
          this.quartersLoading = false;
        });
    },
    onSortChange({ prop, order }) {
      if (!order) {
        this.sortBy = DEFAULT_SORT.sort_by;
        this.sortOrder = DEFAULT_SORT.sort_order;
      } else {
        this.sortBy = prop;
        this.sortOrder = order === "ascending" ? "asc" : "desc";
      }
      this.fetchProjects();
    },
    handlePageChange(page) {
      this.page = page;
      this.fetchProjects();
    },
    handleSizeChange(size) {
      this.pageSize = size;
      this.page = 1;
      this.fetchProjects();
    },
    clearFilters() {
      this.filters = {
        year: null,
        region_id: null,
        district_id: null,
        quarter_id: null,
        status: null,
        category: null,
        outcome_type: null,
        ai_status: null,
        search: "",
      };
      this.districts = [];
      this.quarters = [];
      this.sortBy = DEFAULT_SORT.sort_by;
      this.sortOrder = DEFAULT_SORT.sort_order;
      this.applyFilters();
    },
    exportProjectsExcel() {
      this.exporting = true;
      this.$http
        .get("api/v1/quarter-projects/excel", {
          // WYSIWYG: joriy filtr+saralash, limit/offset'siz
          params: this.buildQueryParams(false),
          responseType: "blob",
        })
        .then((res) => {
          this.downloadBlob(res, "Quarter-Projects.xlsx");
        })
        .catch(({ response }) => {
          ElNotification({
            title: "Ogohlantirish",
            message:
              response?.data?.message ||
              "Excel yuklashda xatolik sodir bo'ldi!",
            type: "warning",
          });
        })
        .finally(() => {
          this.exporting = false;
        });
    },

    // ─────────────── Arxivlash / O'chirish ───────────────
    canArchive(row) {
      return row.status !== "ARCHIVE";
    },
    canDelete(row) {
      return row.status !== "ACTIVE" && Number(row.linked_paydocs_count ?? 0) === 0;
    },
    deleteDisabledReason(row) {
      if (row.status === "ACTIVE") {
        return "Faol loyihani o'chirib bo'lmaydi — avval arxivlang";
      }
      const count = Number(row.linked_paydocs_count ?? 0);
      if (count > 0) {
        return `Bu loyihaga ${count} ta to'lov hujjati bog'langan, o'chirib bo'lmaydi`;
      }
      return "";
    },
    escapeHtml(value) {
      return String(value ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
    },
    refreshAfterAction({ removedRow = false } = {}) {
      if (removedRow && this.projects.length <= 1 && this.page > 1) {
        this.page -= 1;
      }
      this.fetchProjects();
    },
    closeDetailIfSame(row) {
      if (this.selectedProject && this.selectedProject.id === row.id) {
        this.detailVisible = false;
        this.selectedProject = null;
      }
    },
    archiveProject(row) {
      const title = this.escapeHtml(row.title);
      let message = `<b>${title}</b> loyihasini arxivlashni tasdiqlaysizmi?`;

      if (row.status === "ACTIVE" && Number(row.available_amount ?? 0) > 0) {
        message +=
          `<p class="qp-confirm-warn">Diqqat: bu loyihada hali ` +
          `<b>${this.formatSum(row.available_amount)} so'm</b> ishlatilmagan. ` +
          `Arxivlangach bu summa mahalla balansiga qaytadi va boshqa loyihalar ` +
          `uchun ishlatilishi mumkin bo'ladi.</p>`;
      }

      ElMessageBox.confirm(message, "Diqqat!", {
        confirmButtonText: "Arxivlash",
        cancelButtonText: "Bekor qilish",
        type: "warning",
        dangerouslyUseHTMLString: true,
      })
        .then(() => {
          this.archivingId = row.id;
          return this.$http.post(`api/v1/quarter-projects/${row.id}/archive`);
        })
        .then(() => {
          ElNotification({
            title: "Muvaffaqiyatli",
            message: "Loyiha arxivlandi",
            type: "success",
          });
          this.closeDetailIfSame(row);
          this.refreshAfterAction();
        })
        .catch((error) => {
          if (error === "cancel" || error === "close") return;

          const response = error?.response;
          if (response?.status === 401) return;

          ElNotification({
            title: "Xatolik",
            message:
              response?.data?.message ||
              "Loyihani arxivlashda xatolik sodir bo'ldi!",
            type: "error",
          });

          if (response?.status === 400 || response?.status === 404) {
            this.refreshAfterAction();
          }
        })
        .finally(() => {
          this.archivingId = null;
        });
    },
    deleteProject(row) {
      const title = this.escapeHtml(row.title);
      const message =
        `<b>${title}</b> loyihasini o'chirasizmi?` +
        `<p class="qp-confirm-warn qp-confirm-warn--danger">Bu amalni qaytarib bo'lmaydi — o'chirilgan ` +
        `loyihani tiklash imkoniyati yo'q. Loyiha ro'yxatdan, Excel eksportidan ` +
        `va mahalla balans hisob-kitoblaridan butunlay chiqib ketadi.</p>`;

      ElMessageBox.confirm(message, "Diqqat!", {
        confirmButtonText: "Ha, o'chirilsin",
        cancelButtonText: "Bekor qilish",
        type: "error",
        confirmButtonClass: "el-button--danger",
        dangerouslyUseHTMLString: true,
      })
        .then(() => {
          this.deletingId = row.id;
          return this.$http.delete(`api/v1/quarter-projects/${row.id}`);
        })
        .then(() => {
          ElNotification({
            title: "Muvaffaqiyatli",
            message: "Loyiha o'chirildi",
            type: "success",
          });
          this.closeDetailIfSame(row);
          this.refreshAfterAction({ removedRow: true });
        })
        .catch((error) => {
          if (error === "cancel" || error === "close") return;

          const response = error?.response;
          if (response?.status === 401) return;

          ElNotification({
            title: "Xatolik",
            message:
              response?.data?.message ||
              "Loyihani o'chirishda xatolik sodir bo'ldi!",
            type: "error",
          });

          if (response?.status === 400 || response?.status === 404) {
            this.refreshAfterAction();
          }
        })
        .finally(() => {
          this.deletingId = null;
        });
    },

    openDetail(row) {
      this.selectedProject = row;
      this.detailVisible = true;
    },
    fileUrl(file) {
      return this.fileBaseUrl + (file?.id ?? "");
    },
    fileBaseName(file) {
      const raw = String(file?.name || "").split("/").pop();
      return raw || "fayl";
    },
    formatFileSize(bytes) {
      if (!bytes && bytes !== 0) return "";
      const units = ["B", "KB", "MB", "GB"];
      let value = Number(bytes);
      let i = 0;
      while (value >= 1024 && i < units.length - 1) {
        value /= 1024;
        i += 1;
      }
      return `${value.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
    },

    // ── label / format helperlar ──
    formatSum(tiyin) {
      if (tiyin === null || tiyin === undefined) return "—";
      return formatterPrice(String(roundNumber(Number(tiyin) / 100)));
    },
    formatDate(value) {
      if (!value) return "—";
      return this.$moment(value).format("DD.MM.YYYY HH:mm");
    },
    labelFrom(options, value) {
      const found = options.find((o) => o.value === value);
      return found ? found.label : value || "—";
    },
    statusLabel(v) {
      return this.labelFrom(this.statusOptions, v);
    },
    statusTagType(v) {
      switch (v) {
        case "ACTIVE":
          return "success";
        case "COMPLETED":
          return "";
        case "AI_REJECTED":
        case "CANCELLED":
          return "danger";
        case "AI_REVIEWING":
          return "warning";
        case "ARCHIVE":
        default:
          return "info";
      }
    },
    categoryLabel(v) {
      return this.labelFrom(this.categoryOptions, v);
    },
    outcomeLabel(v) {
      return this.labelFrom(this.outcomeOptions, v);
    },
    aiStatusLabel(v) {
      return this.labelFrom(this.aiStatusOptions, v);
    },
    aiStatusTagType(v) {
      switch (v) {
        case "APPROVED":
          return "success";
        case "REJECTED":
          return "danger";
        case "PENDING":
        default:
          return "warning";
      }
    },
  },
};
</script>

<template>
  <div>
    <div class="actions-panel">
      <input
        ref="fileInput"
        class="hidden-file-input"
        type="file"
        :accept="uploadAccept"
        @change="handleFileChange"
      />

      <div class="action-cards">
        <div class="action-card download-card">
          <div class="action-card__body">
            <h3 class="action-card__title">Excel yuklab olish</h3>
            <p class="action-card__description">
              Kerakli yilni tanlang va hisobotni Excel formatida yuklab oling.
            </p>
          </div>

          <div class="action-card__controls download-controls">
            <el-select
              id="year"
              v-model="fin_year"
              size="large"
              placeholder="Yil"
              class="year-select"
            >
              <el-option
                v-for="y in years"
                :key="y"
                :value="y"
                :label="y"
              />
            </el-select>

            <el-button
              size="large"
              :loading="loading"
              :disabled="loading || uploading"
              class="action-button"
              type="success"
              @click="downloadExcel"
            >
              <img
                class="action-button__icon"
                alt=""
                src="@/assets/images/icons/download.svg"
              />
              Excel yuklab olish
            </el-button>
          </div>
        </div>

        <div class="action-card upload-card">
          <div class="action-card__body">
            <h3 class="action-card__title">
              Mahalla budjetining yillik prognozini yuklash
            </h3>
            <p class="action-card__description">
              Tayyor Excel faylni shu alohida bo'lim orqali yuklang.
            </p>
          </div>

          <div class="action-card__controls">
            <el-button
              size="large"
              :loading="uploading"
              :disabled="loading || uploading"
              class="action-button upload-button"
              @click="triggerFileUpload"
            >
              <img
                class="action-button__icon"
                alt=""
                src="@/assets/images/icons/file-attach-white.svg"
              />
              Prognoz faylini yuklash
            </el-button>

            <span
              v-if="uploadedFileName"
              class="upload-success-icon"
              aria-label="Fayl yuklandi"
              title="Fayl yuklandi"
            >
              <svg
                viewBox="0 0 24 24"
                width="20"
                height="20"
                aria-hidden="true"
              >
                <path
                  d="M20 6L9 17l-5-5"
                  fill="none"
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2.5"
                />
              </svg>
            </span>
          </div>
        </div>

        <div class="action-card balance-card">
          <div class="action-card__body">
            <h3 class="action-card__title">Mahalla balansini yuklab olish</h3>
            <p class="action-card__description">
              Mahalla balansi hisobotini Excel formatida yuklab oling.
            </p>
          </div>

          <div class="action-card__controls">
            <el-button
              size="large"
              :loading="loadingBalance"
              :disabled="loadingBalance || uploading"
              class="action-button balance-button"
              @click="downloadBalanceExcel"
            >
              <img
                class="action-button__icon"
                alt=""
                src="@/assets/images/icons/download.svg"
              />
              Balansni yuklab olish
            </el-button>
          </div>
        </div>
      </div>
    </div>

    <!-- ───────────────── Mahalla loyihalari ───────────────── -->
    <section class="qp-section">
      <div class="qp-section__head">
        <div class="qp-section__title-wrap">
          <h2 class="qp-section__title">Mahalla loyihalari</h2>
          <span class="qp-section__count"> Jami: {{ total }} ta loyiha </span>
        </div>

        <div class="qp-section__actions">
          <el-button
            text
            class="qp-toggle"
            @click="filtersVisible = !filtersVisible"
          >
            {{ filtersVisible ? "Filtrlarni yashirish" : "Filtrlarni ko'rsatish" }}
          </el-button>
          <el-button
            :loading="exporting"
            :disabled="exporting"
            type="success"
            @click="exportProjectsExcel"
          >
            <img
              class="action-button__icon"
              alt=""
              src="@/assets/images/icons/download.svg"
            />
            Excel yuklab olish
          </el-button>
        </div>
      </div>

      <el-collapse-transition>
        <div v-show="filtersVisible" class="qp-filters">
          <div class="qp-filters__grid">
            <el-select
              v-model="filters.year"
              placeholder="Yil"
              clearable
              @change="applyFilters"
            >
              <el-option
                v-for="y in years"
                :key="y"
                :value="y"
                :label="y"
              />
            </el-select>

            <el-select
              v-model="filters.region_id"
              placeholder="Viloyat"
              clearable
              filterable
              :loading="regionsLoading"
              @change="onRegionChange"
            >
              <el-option
                v-for="region in regions"
                :key="region.id"
                :value="region.id"
                :label="region.title"
              />
            </el-select>

            <el-select
              v-model="filters.district_id"
              placeholder="Tuman"
              clearable
              filterable
              :disabled="!filters.region_id"
              :loading="districtsLoading"
              @change="onDistrictChange"
            >
              <el-option
                v-for="district in districts"
                :key="district.id"
                :value="district.id"
                :label="district.title"
              />
            </el-select>

            <el-select
              v-model="filters.quarter_id"
              placeholder="Mahalla"
              clearable
              filterable
              :disabled="!filters.district_id"
              :loading="quartersLoading"
              @change="applyFilters"
            >
              <el-option
                v-for="quarter in quarters"
                :key="quarter.id"
                :value="quarter.id"
                :label="quarter.title"
              />
            </el-select>

            <el-select
              v-model="filters.status"
              placeholder="Holat"
              clearable
              @change="applyFilters"
            >
              <el-option
                v-for="opt in statusOptions"
                :key="opt.value"
                :value="opt.value"
                :label="opt.label"
              />
            </el-select>

            <el-select
              v-model="filters.category"
              placeholder="Kategoriya"
              clearable
              @change="applyFilters"
            >
              <el-option
                v-for="opt in categoryOptions"
                :key="opt.value"
                :value="opt.value"
                :label="opt.label"
              />
            </el-select>

            <el-select
              v-model="filters.outcome_type"
              placeholder="Natija turi"
              clearable
              @change="applyFilters"
            >
              <el-option
                v-for="opt in outcomeOptions"
                :key="opt.value"
                :value="opt.value"
                :label="opt.label"
              />
            </el-select>

            <el-select
              v-model="filters.ai_status"
              placeholder="AI holati"
              clearable
              @change="applyFilters"
            >
              <el-option
                v-for="opt in aiStatusOptions"
                :key="opt.value"
                :value="opt.value"
                :label="opt.label"
              />
            </el-select>

            <el-input
              v-model="filters.search"
              class="qp-filters__search"
              placeholder="Nom, tavsif yoki loyiha kodi bo'yicha qidirish"
              clearable
              @input="debouncedSearch"
              @clear="applyFilters"
            >
              <template #prefix>
                <span class="qp-search-icon">🔍</span>
              </template>
            </el-input>

            <el-button
              class="qp-clear-btn"
              :disabled="!hasActiveFilters"
              @click="clearFilters"
            >
              Filtrlarni tozalash
            </el-button>
          </div>
        </div>
      </el-collapse-transition>

      <el-table
        v-loading="tableLoading"
        :data="projects"
        border
        stripe
        class="qp-table"
        :default-sort="defaultSort"
        height="calc(100vh - 430px)"
        @sort-change="onSortChange"
      >
        <template #empty>
          <div class="qp-empty">
            <p>Berilgan filtrlar bo'yicha loyihalar topilmadi</p>
            <el-button
              v-if="hasActiveFilters"
              size="small"
              @click="clearFilters"
            >
              Filtrlarni tozalash
            </el-button>
          </div>
        </template>

        <el-table-column type="index" label="#" width="56" align="center" />

        <el-table-column label="Loyiha kodi" min-width="150">
          <template #default="{ row }">
            <span class="qp-mono">{{ row.public_id }}</span>
          </template>
        </el-table-column>

        <el-table-column
          prop="title"
          label="Nomi"
          min-width="220"
          sortable="custom"
          show-overflow-tooltip
        >
          <template #default="{ row }">
            <el-tooltip
              v-if="row.description"
              :content="row.description"
              placement="top"
              effect="dark"
            >
              <span class="qp-title">{{ row.title }}</span>
            </el-tooltip>
            <span v-else class="qp-title">{{ row.title }}</span>
          </template>
        </el-table-column>

        <el-table-column
          prop="region"
          label="Viloyat"
          min-width="140"
          sortable="custom"
          show-overflow-tooltip
        />
        <el-table-column
          prop="district"
          label="Tuman"
          min-width="140"
          sortable="custom"
          show-overflow-tooltip
        />
        <el-table-column
          prop="quarter"
          label="Mahalla"
          min-width="160"
          sortable="custom"
          show-overflow-tooltip
        />

        <el-table-column label="Kategoriya" min-width="150">
          <template #default="{ row }">
            {{ categoryLabel(row.category) }}
          </template>
        </el-table-column>

        <el-table-column label="Natija turi" min-width="120">
          <template #default="{ row }">
            {{ outcomeLabel(row.outcome_type) }}
          </template>
        </el-table-column>

        <el-table-column
          prop="planned_amount"
          label="Rejalashtirilgan"
          min-width="150"
          align="right"
          sortable="custom"
        >
          <template #default="{ row }">
            <span class="qp-amount">{{ formatSum(row.planned_amount) }}</span>
          </template>
        </el-table-column>
        <el-table-column
          prop="paid_amount"
          label="To'langan"
          min-width="150"
          align="right"
          sortable="custom"
        >
          <template #default="{ row }">
            <span class="qp-amount">{{ formatSum(row.paid_amount) }}</span>
          </template>
        </el-table-column>
        <el-table-column
          prop="available_amount"
          label="Mavjud"
          min-width="150"
          align="right"
          sortable="custom"
        >
          <template #default="{ row }">
            <span class="qp-amount">{{ formatSum(row.available_amount) }}</span>
          </template>
        </el-table-column>

        <el-table-column
          prop="status"
          label="Holat"
          min-width="150"
          sortable="custom"
        >
          <template #default="{ row }">
            <el-tag
              :type="statusTagType(row.status)"
              effect="light"
              round
              size="small"
            >
              {{ statusLabel(row.status) }}
            </el-tag>
          </template>
        </el-table-column>

        <el-table-column label="AI holati" min-width="130">
          <template #default="{ row }">
            <el-tag
              v-if="row.ai_status"
              :type="aiStatusTagType(row.ai_status)"
              effect="plain"
              size="small"
            >
              {{ aiStatusLabel(row.ai_status) }}
            </el-tag>
            <span v-else>—</span>
          </template>
        </el-table-column>

        <el-table-column
          prop="linked_paydocs_count"
          label="To'lovlar"
          min-width="110"
          align="center"
          sortable="custom"
        >
          <template #default="{ row }">
            {{ row.linked_paydocs_count ?? 0 }}
          </template>
        </el-table-column>

        <el-table-column
          prop="created_at"
          label="Yaratilgan sana"
          min-width="160"
          sortable="custom"
        >
          <template #default="{ row }">
            {{ formatDate(row.created_at) }}
          </template>
        </el-table-column>

        <el-table-column
          label="Amallar"
          :width="isAdmin ? 150 : 116"
          align="center"
          fixed="right"
        >
          <template #default="{ row }">
            <div class="qp-row-actions">
              <el-button
                type="primary"
                circle
                size="small"
                title="Batafsil ko'rish"
                @click="openDetail(row)"
              >
                <el-icon><View /></el-icon>
              </el-button>

              <el-tooltip
                v-if="!canArchive(row)"
                content="Allaqachon arxivlangan"
                placement="top"
              >
                <span class="qp-action-wrap">
                  <el-button type="warning" circle size="small" disabled>
                    <el-icon><FolderDelete /></el-icon>
                  </el-button>
                </span>
              </el-tooltip>
              <el-button
                v-else
                type="warning"
                circle
                size="small"
                title="Arxivlash"
                :loading="archivingId === row.id"
                :disabled="archivingId === row.id || deletingId === row.id"
                @click="archiveProject(row)"
              >
                <el-icon><FolderDelete /></el-icon>
              </el-button>

              <template v-if="isAdmin">
                <el-tooltip
                  v-if="!canDelete(row)"
                  :content="deleteDisabledReason(row)"
                  placement="top"
                >
                  <span class="qp-action-wrap">
                    <el-button type="danger" circle size="small" disabled>
                      <el-icon><Delete /></el-icon>
                    </el-button>
                  </span>
                </el-tooltip>
                <el-button
                  v-else
                  type="danger"
                  circle
                  size="small"
                  title="O'chirish"
                  :loading="deletingId === row.id"
                  :disabled="archivingId === row.id || deletingId === row.id"
                  @click="deleteProject(row)"
                >
                  <el-icon><Delete /></el-icon>
                </el-button>
              </template>
            </div>
          </template>
        </el-table-column>
      </el-table>

      <div class="qp-pagination">
        <el-pagination
          v-model:current-page="page"
          v-model:page-size="pageSize"
          :total="total"
          :page-sizes="[10, 20, 50, 100]"
          layout="total, sizes, prev, pager, next, jumper"
          background
          @current-change="handlePageChange"
          @size-change="handleSizeChange"
        />
      </div>
    </section>

    <!-- ───────────────── Loyiha tafsilotlari modal ───────────────── -->
    <el-dialog
      v-model="detailVisible"
      width="760px"
      top="5vh"
      class="qp-dialog"
      align-center
      destroy-on-close
      :show-close="false"
    >
      <template #header="{ close }">
        <div v-if="selectedProject" class="qp-dhead">
          <div class="qp-dhead__left">
            <div class="qp-dhead__icon">
              <el-icon><Document /></el-icon>
            </div>
            <div class="qp-dhead__text">
              <h3 class="qp-dhead__title">{{ selectedProject.title }}</h3>
              <span class="qp-dhead__id qp-mono">{{
                selectedProject.public_id
              }}</span>
            </div>
          </div>
          <el-button class="qp-dhead__close" text circle @click="close">
            <el-icon><Close /></el-icon>
          </el-button>
        </div>
      </template>

      <div v-if="selectedProject" class="qp-detail">
        <div class="qp-detail__tags">
          <el-tag
            :type="statusTagType(selectedProject.status)"
            effect="dark"
            round
            size="small"
          >
            {{ statusLabel(selectedProject.status) }}
          </el-tag>
          <el-tag
            v-if="selectedProject.ai_status"
            :type="aiStatusTagType(selectedProject.ai_status)"
            effect="light"
            round
            size="small"
          >
            AI: {{ aiStatusLabel(selectedProject.ai_status) }}
          </el-tag>
          <el-tag effect="plain" round size="small" class="qp-tag-neutral">
            {{ categoryLabel(selectedProject.category) }}
          </el-tag>
          <el-tag effect="plain" round size="small" class="qp-tag-neutral">
            {{ outcomeLabel(selectedProject.outcome_type) }}
          </el-tag>
        </div>

        <p v-if="selectedProject.description" class="qp-detail__desc">
          {{ selectedProject.description }}
        </p>

        <el-alert
          v-if="selectedProject.ai_reject_reason"
          type="error"
          :closable="false"
          show-icon
          class="qp-detail__reject"
        >
          <template #title>AI rad etish sababi</template>
          {{ selectedProject.ai_reject_reason }}
        </el-alert>

        <!-- Moliyaviy blok -->
        <div class="qp-money">
          <div class="qp-money__cards">
            <div class="qp-money-card">
              <span class="qp-money-card__label">Rejalashtirilgan</span>
              <span class="qp-money-card__value">
                {{ formatSum(selectedProject.planned_amount) }}
                <small>so'm</small>
              </span>
            </div>
            <div class="qp-money-card qp-money-card--paid">
              <span class="qp-money-card__label">To'langan</span>
              <span class="qp-money-card__value">
                {{ formatSum(selectedProject.paid_amount) }}
                <small>so'm</small>
              </span>
            </div>
            <div class="qp-money-card qp-money-card--available">
              <span class="qp-money-card__label">Mavjud</span>
              <span class="qp-money-card__value">
                {{ formatSum(selectedProject.available_amount) }}
                <small>so'm</small>
              </span>
            </div>
          </div>
          <div class="qp-money__progress">
            <div class="qp-money__progress-head">
              <span>To'lov bajarilishi</span>
              <span class="qp-money__percent">{{ paidPercent }}%</span>
            </div>
            <div class="qp-progress-track">
              <div
                class="qp-progress-fill"
                :style="{ width: paidPercent + '%' }"
              />
            </div>
          </div>
        </div>

        <!-- Umumiy ma'lumot -->
        <div class="qp-detail__section">
          <span class="qp-detail__section-title">Umumiy ma'lumot</span>
          <div class="qp-detail__grid">
            <div class="qp-field">
              <span class="qp-field__label">Hudud</span>
              <span class="qp-field__value">
                {{ selectedProject.region }} · {{ selectedProject.district }} ·
                {{ selectedProject.quarter }}
              </span>
            </div>
            <div class="qp-field">
              <span class="qp-field__label">Yaratgan shaxs</span>
              <span class="qp-field__value">{{
                selectedProject.created_by_fullname || "—"
              }}</span>
            </div>
            <div class="qp-field">
              <span class="qp-field__label">Bog'langan to'lovlar</span>
              <span class="qp-field__value">{{
                selectedProject.linked_paydocs_count ?? 0
              }}</span>
            </div>
            <div class="qp-field">
              <span class="qp-field__label">Mahalla ID</span>
              <span class="qp-field__value">{{
                selectedProject.quarter_id
              }}</span>
            </div>
            <div class="qp-field">
              <span class="qp-field__label">Yaratilgan sana</span>
              <span class="qp-field__value">{{
                formatDate(selectedProject.created_at)
              }}</span>
            </div>
            <div class="qp-field">
              <span class="qp-field__label">Yangilangan sana</span>
              <span class="qp-field__value">{{
                formatDate(selectedProject.updated_at)
              }}</span>
            </div>
          </div>
        </div>

        <!-- Fayllar -->
        <div class="qp-detail__section">
          <span class="qp-detail__section-title">
            Fayllar
            <span class="qp-files__count">
              {{ selectedProject.files_details?.length || 0 }}
            </span>
          </span>

          <div
            v-if="selectedProject.files_details?.length"
            class="qp-files__list"
          >
            <div
              v-for="file in selectedProject.files_details"
              :key="file.id"
              class="qp-file"
            >
              <div class="qp-file__icon">
                <el-icon><Document /></el-icon>
              </div>
              <div class="qp-file__info">
                <span class="qp-file__name" :title="fileBaseName(file)">
                  {{ fileBaseName(file) }}
                </span>
                <span class="qp-file__meta">
                  {{ formatFileSize(file.size) }}
                </span>
              </div>
              <div class="qp-file__actions">
                <el-button
                  size="small"
                  text
                  type="primary"
                  tag="a"
                  :href="fileUrl(file)"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <el-icon><View /></el-icon>
                  <span>Ko'rish</span>
                </el-button>
                <el-button
                  size="small"
                  type="primary"
                  plain
                  tag="a"
                  :href="fileUrl(file)"
                  :download="fileBaseName(file)"
                >
                  <el-icon><Download /></el-icon>
                  <span>Yuklab olish</span>
                </el-button>
              </div>
            </div>
          </div>

          <div v-else class="qp-files__empty">
            <el-icon><Document /></el-icon>
            <span>Fayllar biriktirilmagan</span>
          </div>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<style scoped>
.actions-panel {
  display: flex;
  gap: 16px;
}

.action-cards {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
}

.action-card {
  max-width: 360px;
  min-width: 300px;
  min-height: 168px;
  padding: 20px;
  border-radius: 18px;
  border: 1px solid #dbe3ef;
  background: linear-gradient(180deg, #ffffff 0%, #f8fbff 100%);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 18px;
}

.download-card {
  box-shadow: 0 10px 30px rgba(16, 24, 40, 0.06);
}

.upload-card {
  border: 1px dashed #f59e0b;
  background: linear-gradient(180deg, #fffaf0 0%, #fff4db 100%);
}

.action-card__body {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.action-card__eyebrow {
  font-size: 12px;
  line-height: 16px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #6b7280;
}

.action-card__title {
  margin: 0;
  font-size: 20px;
  line-height: 28px;
  font-weight: 700;
  color: #111827;
}

.action-card__description {
  margin: 0;
  font-size: 14px;
  line-height: 22px;
  color: #4b5563;
}

.action-card__controls {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.download-controls {
  justify-content: space-between;
}

.year-select {
  width: 120px;
}

.action-button {
  min-height: 44px;
}

.action-button__icon {
  margin-right: 10px;
}

.upload-button {
  background: #d97706;
  border-color: #d97706;
  color: #ffffff;
}

.upload-button:hover,
.upload-button:focus {
  background: #b45309;
  border-color: #b45309;
  color: #ffffff;
}

.balance-card {
  border: 1px solid #bfdbfe;
  background: linear-gradient(180deg, #eff6ff 0%, #dbeafe 100%);
}

.balance-button {
  background: #2563eb;
  border-color: #2563eb;
  color: #ffffff;
}

.balance-button:hover,
.balance-button:focus {
  background: #1d4ed8;
  border-color: #1d4ed8;
  color: #ffffff;
}

.upload-success-icon {
  width: 36px;
  height: 36px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #dcfce7;
  color: #15803d;
  border: 1px solid #86efac;
  flex-shrink: 0;
}

.hidden-file-input {
  display: none;
}

/* ───────────────── Mahalla loyihalari ───────────────── */
.qp-section {
  margin-top: 24px;
  padding: 20px;
  border-radius: 18px;
  border: 1px solid #dbe3ef;
  background: #ffffff;
  box-shadow: 0 10px 30px rgba(16, 24, 40, 0.05);
}

.qp-section__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 16px;
}

.qp-section__title-wrap {
  display: flex;
  align-items: baseline;
  gap: 12px;
  flex-wrap: wrap;
}

.qp-section__title {
  margin: 0;
  font-size: 20px;
  line-height: 28px;
  font-weight: 700;
  color: #111827;
}

.qp-section__count {
  font-size: 13px;
  font-weight: 600;
  color: #2563eb;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  padding: 3px 12px;
  border-radius: 999px;
}

.qp-section__actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.qp-filters {
  padding: 16px;
  margin-bottom: 16px;
  border-radius: 14px;
  background: #f8fafc;
  border: 1px solid #eef2f7;
}

.qp-filters__grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
}

.qp-filters__grid > * {
  width: 100%;
}

.qp-filters__search {
  grid-column: span 3;
}

.qp-clear-btn {
  width: 100%;
}

.qp-search-icon {
  font-size: 13px;
  opacity: 0.6;
}

.qp-table {
  width: 100%;
}

.qp-mono {
  font-family: "SF Mono", "Menlo", "Consolas", monospace;
  font-size: 12px;
  color: #475569;
}

.qp-title {
  font-weight: 600;
  color: #1f2937;
}

.qp-amount {
  font-variant-numeric: tabular-nums;
  font-weight: 600;
  color: #111827;
}

.qp-row-actions {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
}

/* oraliq faqat gap orqali — Element Plus margin'i qatorma-qator farq qiladi */
.qp-row-actions :deep(.el-button + .el-button) {
  margin-left: 0;
}

.qp-action-wrap {
  display: inline-flex;
  vertical-align: middle;
}

.qp-empty {
  padding: 32px 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  color: #6b7280;
}

.qp-pagination {
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
}

/* ═══════════════ Detail modal ═══════════════ */
.qp-dialog :deep(.el-dialog) {
  border-radius: 18px;
  overflow: hidden;
  padding: 0;
}

.qp-dialog :deep(.el-dialog__header) {
  margin: 0;
  padding: 0;
}

.qp-dialog :deep(.el-dialog__body) {
  padding: 22px 24px;
  background: #f8fafc;
}

/* Header */
.qp-dhead {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 18px 20px 18px 22px;
  background: linear-gradient(120deg, #1e3a8a 0%, #2563eb 100%);
  color: #ffffff;
}

.qp-dhead__left {
  display: flex;
  align-items: center;
  gap: 14px;
  min-width: 0;
}

.qp-dhead__icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.16);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  flex-shrink: 0;
}

.qp-dhead__text {
  min-width: 0;
}

.qp-dhead__title {
  margin: 0;
  font-size: 17px;
  line-height: 24px;
  font-weight: 700;
  color: #ffffff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.qp-dhead__id {
  color: rgba(255, 255, 255, 0.75) !important;
  font-size: 12px;
}

.qp-dhead__close {
  color: #ffffff !important;
  font-size: 18px;
  flex-shrink: 0;
}

.qp-dhead__close:hover {
  background: rgba(255, 255, 255, 0.16) !important;
}

/* Body */
.qp-detail {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.qp-detail__tags {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.qp-tag-neutral {
  color: #475569 !important;
  border-color: #cbd5e1 !important;
  background: #ffffff !important;
}

.qp-detail__desc {
  margin: 0;
  font-size: 14px;
  line-height: 22px;
  color: #475569;
  background: #ffffff;
  border: 1px solid #eef2f7;
  border-radius: 12px;
  padding: 14px 16px;
}

.qp-detail__reject {
  border-radius: 12px;
}

/* Moliyaviy blok */
.qp-money {
  background: #ffffff;
  border: 1px solid #eef2f7;
  border-radius: 14px;
  padding: 16px;
}

.qp-money__cards {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
}

.qp-money-card {
  position: relative;
  padding: 14px 16px;
  border-radius: 12px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  display: flex;
  flex-direction: column;
  gap: 6px;
  overflow: hidden;
}

.qp-money-card::before {
  content: "";
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 3px;
  background: #94a3b8;
}

.qp-money-card--paid {
  background: #ecfdf5;
  border-color: #a7f3d0;
}

.qp-money-card--paid::before {
  background: #10b981;
}

.qp-money-card--available {
  background: #eff6ff;
  border-color: #bfdbfe;
}

.qp-money-card--available::before {
  background: #2563eb;
}

.qp-money-card__label {
  font-size: 11px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.qp-money-card__value {
  font-size: 17px;
  font-weight: 700;
  color: #0f172a;
  font-variant-numeric: tabular-nums;
}

.qp-money-card__value small {
  font-size: 12px;
  font-weight: 600;
  color: #94a3b8;
  margin-left: 2px;
}

.qp-money__progress {
  margin-top: 16px;
}

.qp-money__progress-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 13px;
  font-weight: 600;
  color: #475569;
  margin-bottom: 8px;
}

.qp-money__percent {
  color: #10b981;
  font-variant-numeric: tabular-nums;
}

.qp-progress-track {
  height: 8px;
  border-radius: 999px;
  background: #eef2f7;
  overflow: hidden;
}

.qp-progress-fill {
  height: 100%;
  border-radius: 999px;
  background: linear-gradient(90deg, #34d399 0%, #10b981 100%);
  transition: width 0.4s ease;
}

/* Sections */
.qp-detail__section {
  background: #ffffff;
  border: 1px solid #eef2f7;
  border-radius: 14px;
  padding: 16px;
}

.qp-detail__section-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 700;
  color: #0f172a;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 14px;
}

.qp-detail__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px 24px;
}

.qp-field {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}

.qp-field__label {
  font-size: 11px;
  font-weight: 600;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.qp-field__value {
  font-size: 14px;
  color: #1f2937;
  font-weight: 500;
  word-break: break-word;
}

/* Files */
.qp-files__count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: 999px;
  background: #eff6ff;
  color: #2563eb;
  font-size: 12px;
  font-weight: 700;
}

.qp-files__list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.qp-file {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 12px 14px;
  border-radius: 12px;
  border: 1px solid #eef2f7;
  background: #f8fafc;
  transition: border-color 0.2s ease, background 0.2s ease;
}

.qp-file:hover {
  border-color: #bfdbfe;
  background: #ffffff;
}

.qp-file__icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: #fee2e2;
  color: #dc2626;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
}

.qp-file__info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1;
}

.qp-file__name {
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.qp-file__meta {
  font-size: 12px;
  color: #94a3b8;
  font-variant-numeric: tabular-nums;
}

.qp-file__actions {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

.qp-files__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 28px 0;
  color: #94a3b8;
  font-size: 14px;
}

.qp-files__empty .el-icon {
  font-size: 28px;
}

@media (max-width: 640px) {
  .qp-row-actions {
    flex-wrap: wrap;
  }

  .qp-detail__grid,
  .qp-money__cards {
    grid-template-columns: 1fr;
  }

  .qp-file {
    flex-wrap: wrap;
  }
}

@media (max-width: 1200px) {
  .qp-filters__grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .qp-filters__search {
    grid-column: span 2;
  }
}

@media (max-width: 768px) {
  .action-card {
    min-height: auto;
  }

  .action-card__controls,
  .download-controls {
    flex-direction: column;
    align-items: stretch;
  }

  .year-select {
    width: 100%;
  }

  .action-button {
    width: 100%;
  }

  .qp-filters__grid {
    grid-template-columns: 1fr;
  }

  .qp-filters__search {
    grid-column: span 1;
  }
}
</style>

<!-- ElMessageBox <body> ga render bo'ladi — scoped emas -->
<style>
.qp-confirm-warn {
  margin: 10px 0 0;
  padding: 10px 12px;
  border-radius: 8px;
  background: #fef3c7;
  border: 1px solid #fcd34d;
  color: #92400e;
  font-size: 13px;
  line-height: 20px;
}

.qp-confirm-warn--danger {
  background: #fef2f2;
  border-color: #fecaca;
  color: #b91c1c;
}
</style>
